import React, { useState, useMemo, useCallback } from 'react';
import { getSiteIds } from '../helpers';
import PropTypes from 'prop-types';
import { GeneralDialog } from 'sharedComponents/GeneralDialog';
import { translate } from 'helpers/translateHelper';
import { Container } from 'react-bootstrap';
import { GeneralInput } from 'sharedComponents/GeneralInput';
import { findContainingSite, findParentNode, moveAssetsToLocation } from './helpers';
import { LocationHierarchyDropdown } from 'sharedComponents/LocationHierarchyDropdown';
import { NODE_TYPE } from './constants';

const HierarchyAssetMoveModal = ({
    show,
    close,
    selectedAssets,
    selectedIds,
    nodeMap,
    updateHierarchyDataRequest,
    organizations,
    hierarchyData,
    setHierarchyErrorModal,
    setSaveFailed,
    getHierarchyDataRequest,
    setHierarchySelectedAssets,
    selectedSites
}) => {

    const [moveToDestination, setMoveToDestination] = useState('');
    const organizationID = organizations?.[0]?.organizationID;
    const siteId = findContainingSite(nodeMap, selectedIds?.[0]);

    // Find the current parent location of the selected assets
    const currentParent = useMemo(() => {
        return findParentNode(nodeMap, selectedIds?.[0]);
    }, [selectedIds]);

    const handleSubmit = () => {
        const selectedAssetIds = selectedAssets.map(asset => asset.id);
        const requestPayload = moveAssetsToLocation(selectedAssetIds, currentParent?.id, moveToDestination?.id, nodeMap);
        const moveContext = { type: NODE_TYPE.UNASSIGNED_ASSETS, count: selectedAssets.length };
        close();
        const siteIds = getSiteIds(selectedSites);
        updateHierarchyDataRequest([requestPayload], () => {
            getHierarchyDataRequest(organizationID, siteIds);
            setHierarchySelectedAssets([]);
        }, setHierarchyErrorModal, setSaveFailed, moveContext, nodeMap);
    };

    const handleLocationChange = useCallback((nodeData) => {
        if (!nodeData || !nodeData.id) {
            setMoveToDestination(null);
            return;
        }
        setMoveToDestination(nodeData);
    }, [setMoveToDestination]);

    return <GeneralDialog
        show={show}
        close={close}
        title={translate('ABB.Powertrain.Frontend.moveAssetsLabel')}
        modalClassName='hierarchy-move-asset-dialog'
        backdropClassName='hierarchy-move-asset-backdrop'
        cancelButtonProps={{
            text: translate('ABB.Powertrain.Frontend.cancelLabel'),
        }}
        acceptButtonProps={{
            text: translate('ABB.Powertrain.Frontend.MoveLabel'),
            onClick: () => handleSubmit(),
            disabled: !moveToDestination || !selectedAssets?.length
        }}
        closeButton={true}
    >
        <Container fluid className='move-asset-container'>
            <GeneralInput
                className='location-input'
                placeholder={translate('ABB.Powertrain.Frontend.moveAssetFromLabel')}
                value={currentParent?.name || ''}
                label={translate('ABB.Powertrain.Frontend.moveAssetFromLabel')}
                disabled={true}
                title={currentParent?.name || ''} // Show full path as tooltip
            />
            <div className='destination-content'>
                <label>{translate('ABB.Powertrain.Frontend.moveAssetToLabel')}</label>
                <LocationHierarchyDropdown
                    hierarchyData={hierarchyData}
                    selectedSite={{ siteId: siteId }}
                    selectedLocation={moveToDestination}
                    organizationId={organizationID}
                    onSelect={handleLocationChange}
                    placeholder={translate('ABB.Powertrain.Frontend.select')}
                    isForEditHierarchy={true}
                    parentNodeId={currentParent?.id}
                    selectedNodeId={currentParent?.id}
                />
                <span>{translate('ABB.Powertrain.Frontend.moveAssetsDescription')}</span>
            </div>
        </Container>
    </GeneralDialog>;
};

HierarchyAssetMoveModal.propTypes = {
    show: PropTypes.bool.isRequired,
    close: PropTypes.func.isRequired,
    selectedAssets: PropTypes.array,
    updateHierarchyDataRequest: PropTypes.func.isRequired,
    setHierarchySelectedAssets: PropTypes.func.isRequired,
    getHierarchyDataRequest: PropTypes.func.isRequired,
    organizations: PropTypes.array.isRequired,
    hierarchyData: PropTypes.object,
    setHierarchyErrorModal: PropTypes.func,
    setSaveFailed: PropTypes.func,
    nodeMap: PropTypes.object.isRequired,
    selectedIds: PropTypes.array.isRequired,
    selectedSites: PropTypes.array.isRequired
};

export default HierarchyAssetMoveModal;
